/**
 * What the parser had to skip in an otherwise-valid backup.
 *
 * Shown rather than swallowed: "some of my settings didn't come across" with
 * no explanation is the failure mode that makes people distrust an import
 * feature. Capped, because a bundle from a much newer build could produce one
 * warning per unknown field.
 */
import { AlertTriangle } from 'lucide-react';
import type { BundleWarning } from '../model/settingsBundleTypes';

const MAX_SHOWN = 6;

export function ImportWarningList({ warnings }: { warnings: BundleWarning[] }) {
  if (warnings.length === 0) return null;
  const shown = warnings.slice(0, MAX_SHOWN);
  const hidden = warnings.length - shown.length;

  return (
    <div
      className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3"
      data-testid="settings-import-warnings"
    >
      <div className="mb-1 flex items-center gap-2 text-xs font-medium text-warning">
        <AlertTriangle className="h-3.5 w-3.5" />
        {warnings.length} item{warnings.length === 1 ? '' : 's'} in this backup will be skipped
      </div>
      <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
        {shown.map((warning, index) => (
          <li key={`${warning.code}-${index}`}>{warning.message}</li>
        ))}
        {hidden > 0 && <li>…and {hidden} more.</li>}
      </ul>
    </div>
  );
}
